'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import {
  parseAttribution,
  hasAttributionParams,
  ATTRIBUTION_STORAGE_KEY,
} from '@/lib/tracking/attribution';
import {
  VISIT_ID_STORAGE_KEY,
  startEngagementTracking,
  startClickTracking,
  trackEvent,
} from '@/lib/tracking/engagement';
import { getVisitorId } from '@/lib/tracking/consent';
import { captureClientError } from '@/lib/error-reporting/client';

/** Guards the once-per-session beacon send — see module docstring below. */
const VISIT_SENT_KEY = 'cmg_visit_sent';

// =============================================================================
// PAGE_VIEW DEDUP GUARD — module-level (not per-instance) so React StrictMode's
// synchronous dev double-mount (mount -> cleanup -> remount) AND a layout
// instance surviving across sibling-route navigations both fire exactly one
// page_view per distinct path, never two for the same one. A per-effect-instance
// guard (e.g. a ref) can't see across StrictMode's two separate mount calls;
// only module-level state persists across them the way this needs.
//
// Deliberately a single "last path", not a Set of all-seen paths: revisiting
// an earlier path later in the session (back button, re-navigation) is a
// legitimate NEW page_view and must fire again — this only dedups the
// immediate double-fire case (StrictMode remount, or a layout that
// re-renders without the path actually changing).
// =============================================================================
let lastFiredPagePath: string | null = null;

// Test-only reset — dead-code-eliminated in production builds via the
// NODE_ENV check (same pattern as src/lib/phantom-nav/watcher.ts's
// __resetForTesting). Without this, the guard above leaks state across
// unrelated it() blocks within the same test file (vitest's `isolate: true`
// already gives each *file* a fresh module registry — this is only needed
// for isolation WITHIN one file's test cases).
export const __resetPageViewGuardForTesting: () => void =
  process.env.NODE_ENV !== 'production'
    ? function __resetPageViewGuardForTestingImpl(): void {
        lastFiredPagePath = null;
      }
    : /* istanbul ignore next */ function __resetPageViewGuardForTestingNoop(): void {};

/**
 * Best-effort, Tier-0-safe enrichment snapshot (design contract decision 3 —
 * the ONLY fields this pipeline ever collects; deliberately excludes the
 * BANNED high-entropy list — UA-CH, deviceMemory, hardwareConcurrency,
 * navigator.languages array, raw IANA timezone, colorDepth, cookieEnabled,
 * DNT, canvas). Read once, synchronously, at visit-POST time — none of these
 * are live-updating subscriptions. Exported for direct unit testing.
 *
 * `navigator.connection` (Network Information API) has no Safari/Firefox
 * support and isn't yet in TS's DOM lib — the local cast below is the only
 * place this repo reads it. Wrapped in one try/catch (matching the "must
 * never break the visit beacon" contract every other helper in this pipeline
 * follows): a locked-down browser context throwing on matchMedia/connection
 * access degrades to an empty snapshot, never a thrown error.
 */
export function collectEnrichmentSnapshot(): Record<string, unknown> {
  try {
    const nav = navigator as Navigator & {
      connection?: { effectiveType?: string; saveData?: boolean };
    };

    const snapshot: Record<string, unknown> = {
      screen: {
        w: window.screen.width,
        h: window.screen.height,
        dpr: window.devicePixelRatio,
      },
      isTouch: navigator.maxTouchPoints > 0,
      lang: (navigator.language || '').slice(0, 8),
      colorScheme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
      reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    };

    if (nav.connection) {
      snapshot.conn = {
        type: nav.connection.effectiveType,
        saveData: nav.connection.saveData,
      };
    }

    return snapshot;
  } catch {
    // intentional: enrichment is best-effort progressive detail on top of the
    // core visit row — never worth failing (or even logging) the beacon over.
    return {};
  }
}

/**
 * Bootstraps the friction/vitals collector barrel (`@/lib/tracking/collectors`,
 * Lane COLLECT's file — imported dynamically per the cross-lane contract,
 * trusting it exists by integration time) on browser idle, never on the
 * critical rendering path. Falls back to a flat 2s timeout when
 * `requestIdleCallback` isn't supported (no Safari engine support; also
 * absent from happy-dom/jsdom test environments — intentional, see the
 * paired regression test: in a normal test's lifetime this never actually
 * fires, because `afterEach(cleanup)` unmounts and this cleanup cancels the
 * pending timer/idle callback well under 2s of REAL wall time).
 *
 * A failed or (temporarily, mid-rollout) missing module resolves to a
 * silently-swallowed rejection — same "must never surface to the visitor"
 * contract as every other beacon/collector path in this pipeline.
 */
function scheduleCollectorsBootstrap(): () => void {
  if (typeof window === 'undefined') return () => {};

  let cancelled = false;
  let idleHandle: number | null = null;
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

  function bootstrap(): void {
    if (cancelled) return;
    import('@/lib/tracking/collectors')
      .then((mod) => {
        if (!cancelled) mod.startCollectors();
      })
      .catch(() => {
        // intentional: collectors bootstrap is progressive enhancement, not a
        // product feature the visitor depends on — a missing/failed module
        // (build skew mid-rollout, CSP, offline) must never surface an error.
      });
  }

  if (typeof window.requestIdleCallback === 'function') {
    idleHandle = window.requestIdleCallback(bootstrap);
  } else {
    timeoutHandle = setTimeout(bootstrap, 2000);
  }

  return () => {
    cancelled = true;
    if (idleHandle !== null) window.cancelIdleCallback?.(idleHandle);
    if (timeoutHandle !== null) clearTimeout(timeoutHandle);
  };
}

/**
 * Fire the visit beacon and read back its `{id}` response (wire contract:
 * 200 = visit stored, JSON `{id}` = the new `landing_visits` row's id; 204 =
 * bot/rate-limited/validation-failed, no id). Returns the id string on
 * success, or null (204 / malformed body / network failure). Writing the id
 * to `cmg_visit_id` is the CALLER's job (see the effect body below) —
 * session-scoped, not instance-scoped: the synchronous `cmg_visit_sent`
 * guard (same tick as its check, before this fetch is even awaited)
 * guarantees at most ONE visit fetch is ever in flight per session, so this
 * write is safe even from an effect instance that has since unmounted —
 * there is no competing fetch's id it could clobber.
 *
 * `navigator.sendBeacon` is no longer suitable for THIS call — it fires the
 * request but the browser gives no way to read the response body, and this
 * id is exactly what `startEngagementTracking`/`trackEvent` need
 * (`cmg_visit_id`) to attribute this page's engagement/interaction events
 * back to the visit row. `fetch(..., {keepalive:true})` survives page unload
 * the same way sendBeacon did, while still exposing `res.json()`. Network
 * failures are intentionally swallowed — see the catch below for why.
 */
async function sendVisitBeacon(payload: Record<string, unknown>): Promise<string | null> {
  try {
    const res = await fetch('/api/track/visit', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });

    if (res.status !== 200) return null; // 204: bot/rate-limited/validation-failed — no id

    const data = (await res.json()) as { id?: unknown };
    if (typeof data.id !== 'string' || !data.id) return null;

    return data.id;
  } catch {
    // intentional: this beacon is best-effort telemetry, not a product
    // feature the visitor depends on. A failed network request here
    // (offline, ad-blocker, CSP, flaky connection) must never surface an
    // error to the visitor or generate error-reporting noise — the
    // /admin/traffic dashboard simply undercounts by one visit. Genuinely
    // unexpected states (not network failures) are reported below instead.
    return null;
  }
}

/**
 * VisitBeacon — cookieless landing-visit tracker (the `/ig` bio-link
 * pipeline, v1 visit attribution + v2 engagement/interaction events + v3
 * identity/enrichment/journeys). Renders nothing.
 *
 * Mounted by `src/app/(marketing)/layout.tsx`, `src/app/page.tsx`, and — new
 * in v3, to complete pre-signup funnel visibility — `src/app/auth/layout.tsx`
 * (alongside the pre-existing `TrafficTracker`, not replacing it),
 * `src/app/offer/layout.tsx`, and `src/app/demo/layout.tsx`. Still never
 * `/dashboard`, `/admin`, or any authenticated app route.
 *
 * On mount:
 *   1. If the landing URL carries utm_/ref params, persist the parsed
 *      attribution to sessionStorage (`cmg_attribution`) so later funnel
 *      steps (WhatsApp demo form, org signup) can stamp it — this happens
 *      on EVERY mount with attribution params, even if the visit beacon
 *      already fired earlier this session (a second deep-link click should
 *      still update the carried attribution).
 *   2. Start delegated click tracking (`startClickTracking()`) IMMEDIATELY,
 *      before any network request — a first-paint CTA click must never be
 *      dropped waiting on the visit beacon (/api/track/event tolerates an
 *      absent visitId by design: "better unattributed than dropped").
 *   3. If no visit beacon has been sent yet this session (`cmg_visit_sent`
 *      absent), set that flag SYNCHRONOUSLY — same tick as the check, BEFORE
 *      the POST below is awaited (see the WHY comment at the write site: an
 *      idempotency guard that gains an await between check and write is no
 *      longer a guard) — then POST to /api/track/visit and, once resolved,
 *      store the returned id (`cmg_visit_id`) UNCONDITIONALLY — the id is
 *      session-scoped, not instance-scoped (see the WHY at the write site
 *      below): the synchronous guard above means this is the only visit
 *      fetch ever in flight for the session, so this write must land even
 *      from an instance that has since unmounted (e.g. navigated to another
 *      marketing route before the fetch resolved) — dropping it there
 *      previously meant the id NEVER arrived for the rest of the session.
 *      Exactly one beacon per browser session — never on every page
 *      navigation.
 *   4. Regardless of step 3 (a later page in the same session already has
 *      `cmg_visit_id` from the first page's beacon) — start engagement
 *      tracking for THIS page (gated on `!cancelled`, unlike the id write
 *      above — no point binding listeners for an already-unmounted
 *      instance) and return its cleanup from the effect.
 *      `startEngagementTracking` no longer needs `cmg_visit_id` to already
 *      exist when it starts (see its own docstring — it now reads the id
 *      fresh at each flush and skips sending while absent, without losing
 *      any accrued engagement), so this ordering relative to step 3 is no
 *      longer load-bearing for correctness — it's kept because starting
 *      tracking immediately after the beacon attempt is the natural place.
 *   5. (v3) After that same point, schedule the Lane COLLECT collectors
 *      barrel to bootstrap on idle (`scheduleCollectorsBootstrap()`) —
 *      web-vitals + friction listeners, same "regardless of step 3" gating
 *      as engagement tracking.
 *
 * Separately (v3, NOT inside `run()`'s mount-once effect): a `usePathname()`
 * effect fires a `page_view` event on initial mount AND every path change —
 * this is what covers in-layout client-side navigation (e.g. between
 * `(marketing)` subpages, where THIS component instance never remounts).
 *
 * v3 consent note: `getVisitorId()` is read ONCE, synchronously, at THIS
 * mount's visit-POST time. If a visitor accepts the cookie banner mid-session
 * on a page whose visit row was already sent, that row is NOT retrofitted
 * with a visitor_id — the design contract's decision 1 explicitly keeps this
 * simple: the value of `cmg_vid` is returning-visitor recognition/cohorts
 * across the visitor's FUTURE pages/sessions, not perfect same-visit
 * backfill, and `getVisitorId()` reading fresh on every subsequent mount
 * means the very next page already picks up the newly-accepted cookie
 * correctly.
 */
export default function VisitBeacon() {
  const pathname = usePathname();

  // v3 page_view — fires on initial mount AND every path change (see the
  // module-level guard's docstring above for why the dedup is a single
  // "last path" rather than a Set). Deliberately its OWN effect, separate
  // from the mount-once `run()` effect below: this one legitimately needs to
  // re-run on every pathname change, while the visit-beacon/click-tracking
  // effect must stay mount-once (re-running it would re-send the visit
  // beacon and re-bind engagement listeners on every client-side navigation).
  useEffect(() => {
    if (pathname === lastFiredPagePath) return;
    lastFiredPagePath = pathname;
    trackEvent('page_view', 'page_view', { path: pathname.slice(0, 256) });
  }, [pathname]);

  useEffect(() => {
    let cancelled = false;
    const cleanups: Array<() => void> = [];

    async function run() {
      try {
        const url = new URL(window.location.href);
        const referrer = document.referrer || null;
        const attribution = parseAttribution(url, referrer);

        if (hasAttributionParams(url)) {
          try {
            sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
          } catch {
            // intentional: sessionStorage may be unavailable (private mode,
            // storage quota) — the funnel carry is best-effort, not a hard
            // requirement (see the "lower bound" comment at the callback route).
          }
        }

        // FIX: start click tracking IMMEDIATELY, before the visit beacon's
        // fetch — a first-paint CTA click must never wait on (or be dropped
        // by) the network round-trip below. /api/track/event accepts events
        // without a visitId by design (better unattributed than dropped).
        // Engagement tracking stays gated below — it genuinely needs the
        // visit id to attribute anything.
        cleanups.push(startClickTracking());

        let alreadySent = false;
        try {
          alreadySent = sessionStorage.getItem(VISIT_SENT_KEY) === '1';
        } catch {
          // intentional: read failure treated as "not sent yet" — worst case is
          // one duplicate visit row, never a silently dropped visit.
        }

        if (!alreadySent) {
          // WHY this write is synchronous, same tick as the check above, and
          // BEFORE the `await` below: an idempotency guard that gains an
          // await between check and write is no longer a guard — React
          // Strict Mode's dev double-mount, and the dual mount points
          // (src/app/page.tsx + src/app/(marketing)/layout.tsx) on a
          // client-side navigation, would otherwise both read `alreadySent`
          // as false before either write landed, sending two beacons.
          try {
            sessionStorage.setItem(VISIT_SENT_KEY, '1');
          } catch {
            // intentional: best-effort — a failed write just means a client-side
            // navigation back to a mounting route sends one more beacon.
          }

          // v3: consent-gated visitor id (null unless this browser previously
          // accepted the cookie banner — see consent.ts's getVisitorId
          // docstring) + the Tier-0 enrichment snapshot. Both read
          // synchronously here, once, at this mount's beacon-send time (see
          // the consent note in this component's own docstring above for why
          // a mid-session consent flip is never retrofitted onto this visit).
          const visitorId = getVisitorId();
          const enrichment = collectEnrichmentSnapshot();

          const visitId = await sendVisitBeacon({
            path: `${url.pathname}${url.search}`,
            referrer,
            sessionFirst: true,
            source: attribution.source,
            medium: attribution.medium,
            campaign: attribution.campaign,
            refCode: attribution.refCode,
            viewportW: window.innerWidth,
            ...(visitorId ? { visitorId } : {}),
            ...enrichment,
          });

          // Session-scoped write, not instance-scoped: gating this on
          // `cancelled` was the bug (07-16 re-review) — the synchronous
          // `cmg_visit_sent` guard above means at most ONE visit fetch is
          // ever in flight per session, so there is no competing id this
          // late write could clobber, even from an already-unmounted
          // instance (e.g. navigated into another marketing route before
          // this resolved). Dropping it there permanently starved the rest
          // of the session's `startEngagementTracking` of an id to attribute
          // to — `cancelled` still correctly gates starting engagement
          // tracking itself, just below.
          if (visitId) {
            try {
              sessionStorage.setItem(VISIT_ID_STORAGE_KEY, visitId);
            } catch {
              // intentional: best-effort — a failed write just means this page's
              // engagement/click events go unattributed (visitId stays absent),
              // never a thrown error surfaced to the visitor.
            }
          }
        }

        if (cancelled) return;

        // Engagement tracking covers THIS page regardless of whether the
        // visit beacon was sent this mount (docstring point 4 above) — it
        // genuinely needs the visit id to attribute anything, unlike click
        // tracking above, but (see engagement.ts) no longer needs the id to
        // already exist at start time: it reads fresh at each flush.
        cleanups.push(startEngagementTracking());
        // v3 (docstring point 5): collectors bootstrap, same "regardless of
        // step 3" gating — idle-scheduled, never blocking first paint.
        cleanups.push(scheduleCollectorsBootstrap());
      } catch (err) {
        // Genuinely unexpected failure (e.g. URL construction) — distinct from
        // the intentionally-silent network-failure path above, worth a report.
        // @next-redirect-ok reason=this try block only awaits sendVisitBeacon() (a local
        // fetch() wrapper, not a Next.js server action) and sessionStorage reads/writes —
        // nothing here can ever throw NEXT_REDIRECT, so the guard would be dead weight.
        captureClientError(err, 'client_runtime', { component: 'VisitBeacon' }).catch(() => {
          // intentional: error-reporting itself failing has no recovery path
        });
      }
    }

    void run();

    return () => {
      cancelled = true;
      cleanups.forEach((cleanup) => cleanup());
    };
    // Mount-once by design — no reactive dependencies; see docstring above.
  }, []);

  return null;
}
